import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Phone, Mail, MapPin, Clock, CheckCircle, Send } from "lucide-react";
import { gtmFormSubmit, gtmClick, gtmPhoneClick } from "@/lib/gtm";
import { SEOHead, localBusinessSchema, breadcrumbSchema } from "../components/SEOHead";
import Breadcrumbs from "../components/Breadcrumbs";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PACKAGES = ["Starter — $499", "Business — $899", "eCommerce — $1,499", "Not Sure Yet"];

export default function ContactPage() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", business_name: "", package_interest: "", message: "" });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please fill in your name, email, and message");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/contact`, form);
      gtmFormSubmit("contact_form", { email: form.email, package_interest: form.package_interest });
      setSubmitted(true);
      toast.success("Message sent! We'll be in touch within 24 hours.");
    } catch {
      toast.error("Something went wrong. Please call us on 0466 476 264.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <main className="pt-16" data-testid="contact-page">
      <SEOHead
        title="Contact Us — Get in Touch"
        description="Contact No Fuss Websites for a free quote on professional web design. Call 0466 476 264, email hello@nofusswebsites.com.au, or fill out our contact form. Melbourne-based, serving all of Australia."
        path="/contact"
        schema={[localBusinessSchema(), breadcrumbSchema([{ name: "Home", path: "/" }, { name: "Contact", path: "/contact" }])]}
      />
      <Breadcrumbs items={[{ name: "Contact Us", path: "/contact" }]} />
      {/* Hero */}
      <section className="bg-[#FFD600] border-b-4 border-black py-20">
        <div className="container-main text-center">
          <p className="text-sm font-black uppercase tracking-widest mb-4">Get In Touch</p>
          <h1 className="text-5xl md:text-7xl font-black tracking-tight mb-4" style={{ fontFamily: "Outfit, sans-serif" }}>
            Let's Build Your Website.
          </h1>
          <p className="text-xl font-semibold max-w-lg mx-auto">
            Fill in the form below and we'll get back to you within 24 hours. Or just give us a call!
          </p>
        </div>
      </section>

      <section className="section-padding" data-testid="contact-section">
        <div className="container-main grid grid-cols-1 md:grid-cols-2 gap-12">
          {/* Contact Info */}
          <div>
            <h2 className="text-3xl font-black mb-8" style={{ fontFamily: "Outfit, sans-serif" }}>Speak to a Real Person</h2>
            <div className="space-y-6 mb-10">
              <a href="tel:0466476264" className="flex items-center gap-4 group" data-testid="contact-phone" onClick={() => gtmPhoneClick("contact_page")}>
                <div className="w-14 h-14 bg-[#FFD600] border-2 border-black rounded-2xl flex items-center justify-center group-hover:-translate-y-1 transition-transform" style={{ boxShadow: "3px 3px 0px 0px rgba(0,0,0,1)" }}>
                  <Phone size={22} />
                </div>
                <div>
                  <p className="text-xs font-black uppercase tracking-wider text-gray-500">Phone</p>
                  <p className="text-xl font-black">0466 476 264</p>
                </div>
              </a>
              <a href="mailto:hello@nofusswebsites.com.au" className="flex items-center gap-4 group" data-testid="contact-email">
                <div className="w-14 h-14 bg-[#0055FF] text-white border-2 border-black rounded-2xl flex items-center justify-center group-hover:-translate-y-1 transition-transform" style={{ boxShadow: "3px 3px 0px 0px rgba(0,0,0,1)" }}>
                  <Mail size={22} />
                </div>
                <div>
                  <p className="text-xs font-black uppercase tracking-wider text-gray-500">Email</p>
                  <p className="text-lg font-bold">hello@nofusswebsites.com.au</p>
                </div>
              </a>
              <div className="flex items-center gap-4">
                <div className="w-14 h-14 bg-black text-white border-2 border-black rounded-2xl flex items-center justify-center" style={{ boxShadow: "3px 3px 0px 0px rgba(0,0,0,1)" }}>
                  <MapPin size={22} />
                </div>
                <div>
                  <p className="text-xs font-black uppercase tracking-wider text-gray-500">Location</p>
                  <p className="text-lg font-bold">Melbourne, Victoria, Australia</p>
                </div>
              </div>
              <div className="flex items-center gap-4">
                <div className="w-14 h-14 bg-[#F4F4F5] border-2 border-black rounded-2xl flex items-center justify-center" style={{ boxShadow: "3px 3px 0px 0px rgba(0,0,0,1)" }}>
                  <Clock size={22} />
                </div>
                <div>
                  <p className="text-xs font-black uppercase tracking-wider text-gray-500">Hours</p>
                  <p className="font-bold">Mon–Fri: 9am–6pm AEST</p>
                  <p className="text-sm text-gray-600">Sat: 10am–3pm AEST</p>
                </div>
              </div>
            </div>

            {/* Guarantees */}
            <div className="bg-[#F4F4F5] border-2 border-black rounded-2xl p-6" style={{ boxShadow: "4px 4px 0px 0px rgba(0,0,0,1)" }}>
              <h3 className="font-black text-lg mb-4" style={{ fontFamily: "Outfit, sans-serif" }}>Our Guarantee</h3>
              {["Reply within 24 hours", "No pushy sales tactics", "Honest advice on what you need", "Free quote — no obligation", "100% Aussie team"].map((g, i) => (
                <div key={i} className="flex items-center gap-2 mb-2">
                  <CheckCircle size={16} className="text-green-600" fill="#22C55E" />
                  <span className="text-sm font-semibold">{g}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Form */}
          {submitted ? (
            <div className="bg-[#FFD600] border-2 border-black rounded-2xl p-12 flex flex-col items-center justify-center text-center" style={{ boxShadow: "6px 6px 0px 0px rgba(0,0,0,1)" }} data-testid="contact-success">
              <CheckCircle size={56} className="mb-6" />
              <h2 className="text-3xl font-black mb-4" style={{ fontFamily: "Outfit, sans-serif" }}>Message Sent!</h2>
              <p className="text-lg font-semibold mb-4">Thanks for getting in touch. We'll be back to you within 24 hours.</p>
              <p className="font-bold">Or call us now: <a href="tel:0466476264" className="underline">0466 476 264</a></p>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="bg-white border-2 border-black rounded-2xl p-8" style={{ boxShadow: "6px 6px 0px 0px rgba(0,0,0,1)" }} data-testid="contact-form">
              <h2 className="text-2xl font-black mb-6" style={{ fontFamily: "Outfit, sans-serif" }}>Get a Free Quote</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                <div>
                  <label className="text-sm font-black uppercase tracking-wider mb-1.5 block">Name *</label>
                  <input
                    name="name" value={form.name} onChange={handleChange}
                    placeholder="Jane Smith"
                    className="w-full border-2 border-black rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#FFD600]"
                    data-testid="contact-name-input"
                  />
                </div>
                <div>
                  <label className="text-sm font-black uppercase tracking-wider mb-1.5 block">Email *</label>
                  <input
                    name="email" value={form.email} onChange={handleChange} type="email"
                    placeholder="jane@business.com.au"
                    className="w-full border-2 border-black rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#FFD600]"
                    data-testid="contact-email-input"
                  />
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                <div>
                  <label className="text-sm font-black uppercase tracking-wider mb-1.5 block">Phone</label>
                  <input
                    name="phone" value={form.phone} onChange={handleChange}
                    placeholder="0400 000 000"
                    className="w-full border-2 border-black rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#FFD600]"
                    data-testid="contact-phone-input"
                  />
                </div>
                <div>
                  <label className="text-sm font-black uppercase tracking-wider mb-1.5 block">Business Name</label>
                  <input
                    name="business_name" value={form.business_name} onChange={handleChange}
                    placeholder="Jane's Cafe"
                    className="w-full border-2 border-black rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#FFD600]"
                    data-testid="contact-business-input"
                  />
                </div>
              </div>
              <div className="mb-4">
                <label className="text-sm font-black uppercase tracking-wider mb-1.5 block">Package Interest</label>
                <select
                  name="package_interest" value={form.package_interest} onChange={handleChange}
                  className="w-full border-2 border-black rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#FFD600] bg-white"
                  data-testid="contact-package-select"
                >
                  <option value="">Select a package...</option>
                  {PACKAGES.map(p => <option key={p} value={p}>{p}</option>)}
                </select>
              </div>
              <div className="mb-6">
                <label className="text-sm font-black uppercase tracking-wider mb-1.5 block">Message *</label>
                <textarea
                  name="message" value={form.message} onChange={handleChange}
                  placeholder="Tell us about your business and what you need..."
                  rows={5}
                  className="w-full border-2 border-black rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#FFD600] resize-none"
                  data-testid="contact-message-input"
                />
              </div>
              <button
                type="submit"
                disabled={submitting}
                className="w-full btn-primary text-lg flex items-center justify-center gap-2 disabled:opacity-60"
                data-testid="contact-submit-btn"
              >
                {submitting ? "Sending..." : <><Send size={16} /> Send My Quote Request</>}
              </button>
              <p className="text-xs text-gray-500 text-center mt-4">No spam. No pushy sales. Just a friendly chat about your website.</p>
            </form>
          )}
        </div>
      </section>
    </main>
  );
}
